import type { DrawStage, PlayerStanding } from '@/draw/types';
import type { SeatPlanEntry } from '@/draw/utils/seatPlan';

export interface PoolRow {
  standing: PlayerStanding;
  played: number;
  gameDiff: number;
  gamePct: number | null;
}

export function poolRows(standings: PlayerStanding[]): { rows: PoolRow[]; started: boolean } {
  const rows = standings.map((standing) => {
    const games = standing.gamesWon + standing.gamesLost;
    return {
      standing,
      played: standing.wins + standing.losses + (standing.draws ?? 0),
      gameDiff: standing.gamesWon - standing.gamesLost,
      gamePct: games > 0 ? Math.round((standing.gamesWon / games) * 100) : null,
    };
  });
  return { rows, started: rows.some((r) => r.played > 0) };
}

export function poolNumber(stage: Pick<DrawStage, 'stageId' | 'name'>): number | null {
  const byId = /^qualifying_pool_(\d+)$/.exec(stage.stageId);
  if (byId) return Number(byId[1]);
  const byName = /^Qualifying Pool (\d+)$/.exec(stage.name);
  return byName ? Number(byName[1]) : null;
}

export interface PoolCut {
  from: number;
  to: number;
  stageName: string;
}

/** Where this pool's finishing places go, read off the later stages' seat plans. */
export function poolCuts(pool: DrawStage, stages: DrawStage[]): PoolCut[] {
  const n = poolNumber(pool);
  if (n === null) return [];
  const cuts: PoolCut[] = [];
  for (const stage of stages) {
    const plan = stage.settings?.seatPlan as SeatPlanEntry[] | undefined;
    const ranks = (plan ?? []).filter((e) => e.pool === n).map((e) => e.rank + 1);
    if (ranks.length === 0) continue;
    cuts.push({ from: Math.min(...ranks), to: Math.max(...ranks), stageName: stage.name });
  }
  return cuts.sort((a, b) => a.from - b.from);
}
